/**
 * @fileoverview Tenant Management Page for SuperAdmins
 */
import { useState, useEffect } from 'react';
import { tenantService } from '@/services/tenant/tenantService';
import { useLanguage } from '@/contexts/LanguageContext';
import clsx from 'clsx';

export const TenantManagement = () => {
    const { t } = useLanguage();
    const [tenants, setTenants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        organization_type: 'flight_school',
        blockchain_wallet_address: '',
    });

    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = async () => {
        try {
            setIsLoading(true);
            const data = await tenantService.listTenants();
            setTenants(data);
        } catch (err) {
            setError(t('tenant.load_error', 'Error al cargar organizaciones'));
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (tenant = null) => {
        if (tenant) {
            setEditingTenant(tenant);
            setFormData({
                name: tenant.name,
                slug: tenant.slug,
                organization_type: tenant.organization_type,
                blockchain_wallet_address: tenant.blockchain_wallet_address || '',
            });
        } else {
            setEditingTenant(null);
            setFormData({
                name: '',
                slug: '',
                organization_type: 'flight_school',
                blockchain_wallet_address: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingTenant) {
                await tenantService.updateTenant(editingTenant.id, formData);
            } else {
                await tenantService.createTenant(formData);
            }
            setIsModalOpen(false);
            fetchTenants();
        } catch (err) {
            setError(err.message || t('tenant.save_error', 'Error al guardar organización'));
        }
    };

    const handleToggleStatus = async (tenant) => {
        if (!window.confirm(t('tenant.confirm_toggle', `¿Estás seguro de ${tenant.is_active ? 'desactivar' : 'activar'} esta organización?`))) return;
        try {
            await tenantService.updateTenant(tenant.id, { is_active: !tenant.is_active });
            fetchTenants();
        } catch (err) {
            setError(t('tenant.status_error', 'Error al cambiar estado'));
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{t('tenant.management_title', 'Gestión de Organizaciones')}</h1>
                    <p className="text-slate-500">{t('tenant.management_subtitle', 'Administra los inquilinos y sus configuraciones')}</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-md flex items-center gap-2"
                >
                    <i className="fas fa-plus"></i> {t('tenant.new_org', 'Nueva Organización')}
                </button>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 flex items-center gap-2">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('tenant.organization', 'Organización')}</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('tenant.slug', 'Slug')}</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('tenant.type', 'Tipo')}</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('tenant.users', 'Usuarios')}</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">{t('history.status', 'Estado')}</th>
                            <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">{t('tenant.actions', 'Acciones')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {isLoading ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-12 text-center text-slate-400">{t('common.loading', 'Cargando...')}</td>
                            </tr>
                        ) : tenants.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-12 text-center text-slate-400">{t('tenant.no_orgs', 'No hay organizaciones registradas')}</td>
                            </tr>
                        ) : (
                            tenants.map((tenant) => (
                                <tr key={tenant.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-900">{tenant.name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{tenant.id}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-600">{tenant.slug}</span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600 capitalize">
                                        {tenant.organization_type.replace('_', ' ')}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">
                                        {tenant.user_count}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={clsx(
                                            "px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                                            tenant.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                        )}>
                                            {tenant.is_active ? t('tenant.active', 'Activo') : t('tenant.inactive', 'Inactivo')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleOpenModal(tenant)}
                                                className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                                title={t('common.edit', 'Editar')}
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                onClick={() => handleToggleStatus(tenant)}
                                                className={clsx(
                                                    "p-2 transition-colors",
                                                    tenant.is_active ? "text-slate-400 hover:text-red-600" : "text-slate-400 hover:text-green-600"
                                                )}
                                                title={tenant.is_active ? t('tenant.deactivate', 'Desactivar') : t('tenant.activate', 'Activar')}
                                            >
                                                <i className={tenant.is_active ? "fas fa-ban" : "fas fa-check-circle"}></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-scaleIn">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800">
                                {editingTenant ? t('tenant.edit_org', 'Editar Organización') : t('tenant.new_org', 'Nueva Organización')}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">{t('tenant.name', 'Nombre')}</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">{t('tenant.slug_unique', 'Slug (Único)')}</label>
                                <input
                                    type="text"
                                    required
                                    disabled={!!editingTenant}
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-slate-50"
                                    value={formData.slug}
                                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/ /g, '-') })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">{t('tenant.org_type', 'Tipo de Organización')}</label>
                                <select
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.organization_type}
                                    onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
                                >
                                    <option value="flight_school">{t('tenant.type_flight_school', 'Centro de Instrucción de Aeronáutica Civil (CIAC)')}</option>
                                    <option value="mro_workshop">{t('tenant.type_mro', 'Taller de Mantenimiento (OMA)')}</option>
                                    <option value="authority">{t('tenant.type_authority', 'Autoridad Aeronáutica')}</option>
                                    <option value="other">{t('tenant.type_other', 'Otro')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">{t('tenant.blockchain_wallet', 'Billetera Blockchain (Opcional)')}</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 font-mono text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.blockchain_wallet_address}
                                    onChange={(e) => setFormData({ ...formData, blockchain_wallet_address: e.target.value })}
                                    placeholder="0x..."
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 py-2 text-slate-600 font-bold hover:bg-slate-50 rounded-xl transition-all"
                                >
                                    {t('common.cancel', 'Cancelar')}
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-md shadow-blue-200 transition-all"
                                >
                                    {editingTenant ? t('common.update', 'Actualizar') : t('common.create', 'Crear')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
